import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ReactFlow, {
    addEdge,
    Background,
    Controls,
    MiniMap,
    useNodesState,
    useEdgesState,
    useReactFlow,
    ReactFlowProvider,
    MarkerType,
} from 'reactflow';
import { Button, Space, Spin } from 'antd';
import { SaveOutlined, PartitionOutlined } from '@ant-design/icons';
import 'reactflow/dist/style.css';

import ModuleNode from '../module-group/ModuleNode';
import IconEdge from './IconEdge';
import layoutModules from '../../utils/flow-layout';

const nodeTypes = { moduleNode: ModuleNode };
const edgeTypes = { iconEdge: IconEdge };

const LAYOUT_SAVE_EVENT = 'flow-canvas:save-layout';

const TRIGGER_EDGE_COLORS = {
    no_reply_timeout: '#f5222d',
    scheduled: '#722ed1',
    ats_status_change: '#fa541c',
};

const TRIGGER_ICONS = {
    no_reply_timeout: 'clock',
    scheduled: 'calendar',
    ats_status_change: 'swap',
};

const formatTriggerLabel = (trigger) => {
    const cfg = trigger.config || {};
    switch (trigger.trigger_type) {
        case 'no_reply_timeout':
            return `timeout ${cfg.timeout_minutes || 60}m`;
        case 'scheduled': {
            const dm = cfg.delay_minutes || 60;
            if (dm >= 1440 && dm % 1440 === 0) return `delay ${dm / 1440}d`;
            if (dm >= 60 && dm % 60 === 0) return `delay ${dm / 60}h`;
            return `delay ${dm}m`;
        }
        case 'ats_status_change':
            return `ATS → ${cfg.to_status || '?'}`;
        default:
            return trigger.trigger_type;
    }
};

function buildEdges(modules, positions, { extras = {} } = {}) {
    const newEdges = [];

    modules.forEach((mod) => {
        const sourcePos = positions[mod.id];
        const connections = mod.connection || [];

        connections.forEach((conn) => {
            const isSelfLoop = mod.id === conn.goto;
            const targetPos = positions[conn.goto];
            const isBackEdge = !isSelfLoop && sourcePos && targetPos && targetPos.x <= sourcePos.x;

            newEdges.push({
                id: `conn-${mod.id}-${conn.goto}`,
                source: mod.id,
                target: conn.goto,
                sourceHandle: `conn-${conn.goto}`,
                targetHandle: isSelfLoop ? 'self-target' : undefined,
                label: conn.filters,
                type: 'iconEdge',
                animated: false,
                style: { stroke: '#52c41a', strokeWidth: 2, strokeDasharray: '6,3' },
                markerEnd: { type: MarkerType.ArrowClosed, color: '#52c41a' },
                labelStyle: { fontSize: 10, fontWeight: 500 },
                labelBgStyle: { fill: 'rgba(255,255,255,0.9)', fillOpacity: 0.9 },
                labelBgPadding: [4, 2],
                labelBgBorderRadius: 3,
                data: { icon: 'filter', iconColor: '#52c41a', smoothstep: isSelfLoop || isBackEdge },
            });
        });

        if (mod.fallback) {
            newEdges.push({
                id: `fallback-${mod.id}-${mod.fallback}`,
                source: mod.id,
                target: mod.fallback,
                sourceHandle: 'fallback',
                label: 'fallback',
                type: 'iconEdge',
                animated: false,
                style: { stroke: '#faad14', strokeWidth: 2, strokeDasharray: '5,5' },
                markerEnd: { type: MarkerType.ArrowClosed, color: '#faad14' },
                labelStyle: { fontSize: 10, fontStyle: 'italic' },
                labelBgStyle: { fill: 'rgba(255,255,255,0.9)', fillOpacity: 0.9 },
                labelBgPadding: [4, 2],
                labelBgBorderRadius: 3,
                data: { icon: 'warning', iconColor: '#faad14' },
            });
        }

        const triggers = (mod.triggers || []).filter((t) => !t.deleted_at);
        triggers.forEach((trigger) => {
            const isSelfLoop = mod.id === trigger.goto_module_id;
            const targetPos = positions[trigger.goto_module_id];
            const isBackEdge = !isSelfLoop && sourcePos && targetPos && targetPos.x <= sourcePos.x;
            const edgeColor = TRIGGER_EDGE_COLORS[trigger.trigger_type] || '#cf1322';

            newEdges.push({
                id: `trigger-${trigger.id}`,
                source: mod.id,
                target: trigger.goto_module_id,
                sourceHandle: `trigger-${trigger.id}`,
                targetHandle: isSelfLoop ? 'self-target' : undefined,
                label: formatTriggerLabel(trigger),
                type: 'iconEdge',
                animated: true,
                style: { stroke: edgeColor, strokeWidth: 2, strokeDasharray: '4,4' },
                markerEnd: { type: MarkerType.ArrowClosed, color: edgeColor },
                labelStyle: { fontSize: 10, fontWeight: 500 },
                labelBgStyle: { fill: 'rgba(255,255,255,0.9)', fillOpacity: 0.9 },
                labelBgPadding: [4, 2],
                labelBgBorderRadius: 3,
                data: {
                    icon: TRIGGER_ICONS[trigger.trigger_type] || 'clock',
                    iconColor: edgeColor,
                    smoothstep: isSelfLoop || isBackEdge,
                },
            });
        });

        if (extras.pathForks && Array.isArray(mod.path_forks)) {
            mod.path_forks.forEach((fork, idx) => {
                const target = fork?.module?.id;
                if (!target) return;
                const isSelfLoop = mod.id === target;
                const targetPos = positions[target];
                const isBackEdge = !isSelfLoop && sourcePos && targetPos && targetPos.x <= sourcePos.x;

                newEdges.push({
                    id: `pathfork-${mod.id}-${target}-${idx}`,
                    source: mod.id,
                    target,
                    targetHandle: isSelfLoop ? 'self-target' : undefined,
                    label: fork?.department?.label || 'fork',
                    type: 'iconEdge',
                    animated: false,
                    style: { stroke: '#722ed1', strokeWidth: 2, strokeDasharray: '2,4' },
                    markerEnd: { type: MarkerType.ArrowClosed, color: '#722ed1' },
                    labelStyle: { fontSize: 10, fontWeight: 500 },
                    labelBgStyle: { fill: 'rgba(255,255,255,0.9)', fillOpacity: 0.9 },
                    labelBgPadding: [4, 2],
                    labelBgBorderRadius: 3,
                    data: { icon: 'partition', iconColor: '#722ed1', smoothstep: isSelfLoop || isBackEdge },
                });
            });
        }
    });

    return newEdges;
}

function FlowCanvasInner({
    modules,
    startModuleId,
    savedFlowMeta,
    selectedModuleId,
    onNodeSelect,
    onSaveLayout,
    extras = {},
}) {
    const [nodes, setNodes, onNodesChange] = useNodesState([]);
    const [edges, setEdges, onEdgesChange] = useEdgesState([]);
    const [isReady, setIsReady] = useState(false);
    const [hasLayoutChanged, setHasLayoutChanged] = useState(false);

    useEffect(() => {
        const timer = setTimeout(() => setIsReady(true), 100);
        return () => clearTimeout(timer);
    }, []);

    const savedPositions = useMemo(() => {
        return (savedFlowMeta?.nodes || []).reduce((acc, n) => {
            acc[n.id] = n.position;
            return acc;
        }, {});
    }, [savedFlowMeta]);

    useEffect(() => {
        if (!modules || !modules.length) {
            setNodes([]);
            setEdges([]);
            return;
        }

        const positions = layoutModules(modules, startModuleId);

        setNodes((nds) => modules.map((mod) => {
            const existing = nds.find((n) => n.id === mod.id);
            const position = existing
                ? existing.position
                : (savedPositions[mod.id] || positions[mod.id] || { x: 100, y: 100 });

            return {
                ...(existing || {}),
                id: mod.id,
                type: 'moduleNode',
                position,
                data: {
                    module: mod,
                    isStartModule: mod.id === startModuleId,
                    isSelected: mod.id === selectedModuleId,
                    extras,
                },
                selected: mod.id === selectedModuleId,
            };
        }));

        setEdges(buildEdges(modules, positions, { extras }));
    }, [modules, startModuleId, selectedModuleId, savedPositions, extras, setNodes, setEdges]);

    const onNodeClick = useCallback((_event, node) => {
        if (onNodeSelect) onNodeSelect(node.id);
    }, [onNodeSelect]);

    const onPaneClick = useCallback(() => {
        if (onNodeSelect) onNodeSelect(null);
    }, [onNodeSelect]);

    const onConnect = useCallback(
        (params) => setEdges((eds) => addEdge({ ...params, animated: true }, eds)),
        [setEdges],
    );

    const onEdgeMouseEnter = useCallback((_event, edge) => {
        document.querySelectorAll(`[data-id="${edge.source}"], [data-id="${edge.target}"]`).forEach((el) => {
            el.classList.add('node-highlighted');
        });
    }, []);

    const onEdgeMouseLeave = useCallback(() => {
        document.querySelectorAll('.node-highlighted').forEach((el) => {
            el.classList.remove('node-highlighted');
        });
    }, []);

    const { fitView } = useReactFlow();

    const handleSaveLayout = useCallback(async () => {
        if (!modules.length || !onSaveLayout) return;
        const flowMetaData = {
            nodes: nodes.map((n) => ({ id: n.id, position: n.position })),
        };
        await onSaveLayout(flowMetaData);
        setHasLayoutChanged(false);
    }, [nodes, modules.length, onSaveLayout]);

    // Dirty detection — compare current node positions to saved ones
    useEffect(() => {
        if (!nodes.length) return;
        let isDirty = false;
        for (const node of nodes) {
            const saved = savedPositions[node.id];
            if (!saved
                || Math.round(saved.x) !== Math.round(node.position.x)
                || Math.round(saved.y) !== Math.round(node.position.y)) {
                isDirty = true;
                break;
            }
        }
        setHasLayoutChanged(isDirty);
    }, [nodes, savedPositions]);

    // Allow external callers (e.g. ModuleDetailPanel save) to trigger layout save
    useEffect(() => {
        const handler = () => handleSaveLayout();
        window.addEventListener(LAYOUT_SAVE_EVENT, handler);
        // Legacy event name kept for back-compat with existing ModuleDetailPanel
        window.addEventListener('save-module-group-layout', handler);
        return () => {
            window.removeEventListener(LAYOUT_SAVE_EVENT, handler);
            window.removeEventListener('save-module-group-layout', handler);
        };
    }, [handleSaveLayout]);

    const handleAutoArrange = useCallback(() => {
        if (!modules.length) return;
        const positions = layoutModules(modules, startModuleId);
        setNodes((nds) => nds.map((node) => ({
            ...node,
            position: positions[node.id] || node.position,
        })));
        setTimeout(() => fitView({ padding: 0.2 }), 50);
    }, [modules, startModuleId, setNodes, fitView]);

    if (!isReady) {
        return (
            <div className="fc-loading">
                <Spin tip="Initializing canvas..." />
            </div>
        );
    }

    return (
        <div className="fc-canvas">
            <ReactFlow
                nodes={nodes}
                edges={edges}
                onNodesChange={onNodesChange}
                onEdgesChange={onEdgesChange}
                onConnect={onConnect}
                onNodeClick={onNodeClick}
                onPaneClick={onPaneClick}
                nodeTypes={nodeTypes}
                edgeTypes={edgeTypes}
                onEdgeMouseEnter={onEdgeMouseEnter}
                onEdgeMouseLeave={onEdgeMouseLeave}
                snapToGrid
                snapGrid={[15, 15]}
                fitView
                fitViewOptions={{ padding: 0.2 }}
                minZoom={0.2}
                maxZoom={2}
                proOptions={{ hideAttribution: true }}
            >
                <Background gap={15} size={1} />
                <MiniMap position="bottom-left" pannable={false} zoomable={false} style={{ width: 140, height: 100 }} />
                <Controls position="bottom-right" />
                {onSaveLayout && (
                    <div className="fc-auto-arrange">
                        <Space direction="vertical" size="small">
                            <Button
                                type={hasLayoutChanged ? 'primary' : 'default'}
                                icon={<SaveOutlined />}
                                onClick={handleSaveLayout}
                                title="Save current layout positions"
                                size="small"
                                disabled={!hasLayoutChanged}
                            >
                                Save Layout
                            </Button>
                            <Button
                                type="default"
                                icon={<PartitionOutlined />}
                                onClick={handleAutoArrange}
                                title="Auto-arrange nodes hierarchically"
                                size="small"
                                block
                            >
                                Auto-arrange
                            </Button>
                        </Space>
                    </div>
                )}
            </ReactFlow>
        </div>
    );
}

export default function FlowCanvas(props) {
    return (
        <ReactFlowProvider>
            <FlowCanvasInner {...props} />
        </ReactFlowProvider>
    );
}
